import { ButtonPrimary } from '@/components/button'
import { PasswordInput } from '@/components/input'
import { ROUTE_LOGIN } from '@/constants/Routes'
import { resetPasswordWithToken } from '@/services'
import { createSignUpStep2ValidationSchema } from '@/utils/schema/SignUpStep2ValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

type FormValues = {
  password: string
  confirmPassword: string
}

export const ResetPasswordWithToken = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { token } = useParams()

  const validationSchema = createSignUpStep2ValidationSchema(t)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
  })

  // One submit in flight at a time, so a double click cannot spend a second use of the link.
  const submittingRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = handleSubmit(async (data) => {
    if (!token) {
      toast.error('This reset link is invalid or has expired.')
      return
    }
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    try {
      const res = await resetPasswordWithToken({ token, password: data.password })
      if (res.status === 'SUCCESS') {
        toast.success('Password is updated successfully.')
        navigate(ROUTE_LOGIN, { relative: 'path', replace: true })
      }
    } catch (error: any) {
      toast.error((error?.message as string) || 'This reset link is invalid or has expired.')
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  })

  return (
    <div className="w-screen h-screen bg-white flex sm:justify-center sm:items-center">
      <div className="flex flex-col justify-between sm:justify-start pb-14 sm:pb-0 w-full px-4 sm:px-0 sm:w-[390px] gap-y-4">
        <div className="flex relative flex-col sm:flex-row sm:items-center mt-8 sm:mt-0 gap-y-4 pb-0 sm:pb-6">
          <h1 className="text-2xl xs:text-3xl font-bold text-navy-900">Create New Password</h1>
        </div>

        <div className="flex flex-col gap-y-4 px-3">
          <PasswordInput
            label="Password"
            placeholder="Enter Password"
            labelClassName="!text-navy-900"
            instructs={['Must be at least 6 characters long', 'Must contain one or more numbers']}
            iconClassName="!text-navy-900"
            {...register('password')}
            error={errors.password?.message}
            autoFocus
          />
          <PasswordInput
            label="Confirm Password"
            placeholder="Enter Confirm Password"
            labelClassName="!text-navy-900"
            iconClassName="!text-navy-900"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
            onPaste={(e) => e.preventDefault()}
          />
        </div>
        <div className="flex justify-center items-center px-3">
          <ButtonPrimary title="Submit" className="w-60 !py-[14px]" onClick={onSubmit} disabled={submitting} />
        </div>
      </div>
    </div>
  )
}
